// Global Instructions Rule Applied!
//
// Configuration → Waterfall Templates: the shapes a fund's distribution
// waterfall is created from (cfg.waterfall_templates / _tiers / _splits).
// Reads and writes go through the Python backend's /config routes; every
// response is validated against the zod record before it reaches a screen.
import { API_ROUTES, buildFullUrl, getBackendBaseUrl } from '../../../core/utils/endpoints.ts'
import { readJsonBody } from '../../../core/utils/utilities.ts'
import {
  validateWaterfallTemplateRecord,
  validateWaterfallTemplateRecords,
  type WaterfallTemplateRecord,
  type WaterfallTemplateSavePayload
} from '../../../state_manager/data_models/waterfall_template_record.ts'

const BACKEND_BASE_URL = getBackendBaseUrl()

const authHeaders = (accessToken: string | null): Record<string, string> => {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`
  return headers
}

const errorMessage = async (response: Response, fallback: string): Promise<string> => {
  if (response.status === 401) return 'Authentication failed - please log in again'
  const body = await readJsonBody<{ error?: string; message?: string }>(response)
  return body?.error || body?.message || `${fallback}: ${response.status} ${response.statusText}`
}

export const fetchWaterfallTemplates = async (
  accessToken: string | null = null
): Promise<{ data: WaterfallTemplateRecord[]; error?: string }> => {
  try {
    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.WATERFALL_TEMPLATES_LIST), {
      method: API_ROUTES.CONFIG.WATERFALL_TEMPLATES_LIST.method,
      headers: authHeaders(accessToken)
    })
    if (!response.ok) throw new Error(await errorMessage(response, 'Failed to fetch the waterfall templates'))
    const json = (await response.json()) as { data?: unknown }
    return { data: validateWaterfallTemplateRecords(json.data ?? []) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching waterfall templates:', error)
    return { data: [], error: error instanceof Error ? error.message : 'Failed to fetch the waterfall templates' }
  }
}

/** Upsert a template; the RPC's refusals (a duplicate name, a tier rule) come back as `error`. */
export const saveWaterfallTemplate = async (
  payload: WaterfallTemplateSavePayload,
  accessToken: string | null = null
): Promise<{ data?: WaterfallTemplateRecord; error?: string }> => {
  try {
    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.WATERFALL_TEMPLATE_SAVE), {
      method: API_ROUTES.CONFIG.WATERFALL_TEMPLATE_SAVE.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({ data: payload })
    })
    if (!response.ok) return { error: await errorMessage(response, 'Failed to save the waterfall template') }
    const json = (await response.json()) as { data?: unknown }
    return { data: validateWaterfallTemplateRecord(json.data) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error saving waterfall template:', error)
    return { error: error instanceof Error ? error.message : 'Failed to save the waterfall template' }
  }
}

export const deleteWaterfallTemplate = async (
  templateId: string,
  accessToken: string | null = null
): Promise<{ data?: { status?: string; status_message?: string }; error?: string }> => {
  try {
    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.WATERFALL_TEMPLATE_DELETE, { templateId }),
      { method: API_ROUTES.CONFIG.WATERFALL_TEMPLATE_DELETE.method, headers: authHeaders(accessToken) }
    )
    if (!response.ok) return { error: await errorMessage(response, 'Failed to delete the waterfall template') }
    const json = (await response.json()) as { data?: { status?: string; status_message?: string } }
    return { data: json.data }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error deleting waterfall template:', error)
    return { error: error instanceof Error ? error.message : 'Failed to delete the waterfall template' }
  }
}
